import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  CircularProgress
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import EmailIcon from '@mui/icons-material/Email';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface MFASetupProps {
  token: string;
  onComplete: () => void;
}

export const MFASetup: React.FC<MFASetupProps> = ({ token, onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [mfaType, setMfaType] = useState<'email' | 'totp'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // TOTP setup data
  const [totpSecret, setTotpSecret] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  
  // Backup codes dialog
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodesConfirmed, setBackupCodesConfirmed] = useState(false);

  const steps = ['Select Method', 'Setup', 'Verify', 'Backup Codes'];

  const handleSetupMFA = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/mfa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mfa_type: mfaType })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to setup MFA');
      }
      
      const data = await response.json();
      
      if (mfaType === 'totp') {
        setTotpSecret(data.secret);
        setQrCode(data.qr_code);
      }
      
      if (data.backup_codes) {
        setBackupCodes(data.backup_codes);
        console.log('Backup codes received:', data.backup_codes);
      } else {
        console.warn('No backup codes in response');
      }
      
      setActiveStep(1);
      
    } catch (err: any) {
      setError(err.message || 'Failed to setup MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/mfa/verify-setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: verificationCode,
          mfa_type: mfaType
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Invalid verification code');
      }
      
      const data = await response.json();
      setSuccess(data.message);
      
      // Save backup codes
      if (backupCodes.length > 0) {
        await fetch(`${process.env.REACT_APP_API_URL}/auth/mfa/save-backup-codes`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(backupCodes)
        });
      }
      
      setActiveStep(2);
      setShowBackupCodes(true);
      
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyBackupCodes = () => {
    const text = backupCodes.join('\n');
    navigator.clipboard.writeText(text);
    setSuccess('Backup codes copied to clipboard');
  };

  const handleDownloadBackupCodes = () => {
    const text = backupCodes.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'zynex-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleComplete = () => {
    if (!backupCodesConfirmed) {
      setError('Please confirm you have saved your backup codes');
      return;
    }
    onComplete();
  };

  return (
    <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SecurityIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Multi-Factor Authentication Setup
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Step 0: Select MFA Type */}
      {activeStep === 0 && (
        <Box>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 2 }}>Choose your preferred authentication method:</FormLabel>
            <RadioGroup value={mfaType} onChange={(e) => setMfaType(e.target.value as 'email' | 'totp')}>
              <Paper sx={{ p: 2, mb: 2, border: mfaType === 'email' ? 2 : 1, borderColor: mfaType === 'email' ? 'primary.main' : 'divider' }}>
                <FormControlLabel
                  value="email"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EmailIcon sx={{ mr: 1 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Email OTP</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Receive a 6-digit code via email each time you log in
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Paper>
              <Paper sx={{ p: 2, border: mfaType === 'totp' ? 2 : 1, borderColor: mfaType === 'totp' ? 'primary.main' : 'divider' }}>
                <FormControlLabel
                  value="totp"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PhoneAndroidIcon sx={{ mr: 1 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Authenticator App</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Use Google Authenticator, Authy, or similar app
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Paper>
            </RadioGroup>
          </FormControl>
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            onClick={handleSetupMFA}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Continue'}
          </Button>
        </Box>
      )}

      {/* Step 1: Setup Instructions */}
      {activeStep === 1 && (
        <Box>
          {mfaType === 'totp' ? (
            <>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Scan QR Code with Authenticator App
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                {qrCode && <img src={qrCode} alt="QR Code" style={{ maxWidth: 250 }} />}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Or manually enter this secret key:
              </Typography>
              <TextField
                fullWidth
                value={totpSecret}
                InputProps={{ readOnly: true }}
                sx={{ mb: 3 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                After scanning, enter the 6-digit code from your app to verify:
              </Typography>
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Email OTP is ready! When you log in, we'll send a verification code to your email address.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click Continue to enable Email MFA:
              </Typography>
            </>
          )}
          
          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            sx={{ mb: 2 }}
          />
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                onClick={() => setActiveStep(0)}
                disabled={loading}
              >
                Back
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleVerifySetup}
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify & Enable'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Save Your Backup Codes
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Store these codes in a safe place. Each code can only be used once to access your account if you lose your authentication method.
          </Alert>
          
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              backgroundColor: 'grey.100',
              border: 1,
              borderColor: 'grey.300',
              minHeight: 200
            }}
          >
            {backupCodes && backupCodes.length > 0 ? (
              <Grid container spacing={1.5}>
                {backupCodes.map((code, idx) => (
                  <Grid item xs={6} key={idx}>
                    <Chip
                      label={code}
                      sx={{ 
                        fontFamily: 'monospace', 
                        width: '100%',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        backgroundColor: 'white',
                        color: 'text.primary',
                        border: 1,
                        borderColor: 'grey.300',
                        '& .MuiChip-label': {
                          color: '#000000'
                        }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 150 }}>
                <CircularProgress />
              </Box>
            )}
          </Paper>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyBackupCodes}
              fullWidth
            >
              Copy
            </Button>
            <Button
              variant="outlined"
              onClick={handleDownloadBackupCodes}
              fullWidth
            >
              Download
            </Button>
          </Box>
          
          <FormControlLabel
            control={
              <input
                type="checkbox"
                checked={backupCodesConfirmed}
                onChange={(e) => setBackupCodesConfirmed(e.target.checked)}
              />
            }
            label="I have saved my backup codes in a safe place"
            sx={{ mt: 3 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleComplete}
            disabled={!backupCodesConfirmed}
            fullWidth
          >
            Complete Setup
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
